import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  AbstractField,
  Criterion,
  Field,
  FieldType,
  InformationPageField,
  LongTextField,
  MultipleChoiceField,
  Quota,
  ShortTextField,
  SingleChoiceField,
} from 'queries';
import { useTranslation } from 'react-i18next';
import {
  InformationPageBuilder,
  MultipleChoiceBuilder,
  SelectChoiceBuilder,
  SelectChoicePreview,
} from './FormBuilder/Fields';
import { ParticipantSource } from 'store';
import { ShortText } from './Fields/ShortText';
import { LongText } from './Fields/LongText';
import { PreviewFieldForm } from './FormField/PreviewFieldForm';
import { InformationPage } from './Fields/InformationPage';
import { LogicJumpOption } from './FormBuilder/elements/interface';

export type FormParticipation = {
  id?: string;
  sessionId: string;
  participant: {
    id?: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    source?: ParticipantSource;
  };
};

export enum SubmitType {
  CONTINUE = 'continue',
  DONE = 'done',
}

export interface FieldConfig<T extends AbstractField = Field> {
  label: string;
  icon: string;
  value: FieldType;
  renderBuilder?: (options: {
    field: T;
    mustChooseValidation?: boolean;
    onChange: (field: T) => any;
    criterion?: Criterion;
    onCriterionChange?: (criterion: Criterion) => any;
    logicJumpOptions?: LogicJumpOption[];
    numberOfParticipantRequired?: number;
    sessionId?: string;
    quotas?: Quota[];
    onQuotaChange?: (quotas: Quota[]) => any;
  }) => React.ReactElement;
  renderPreview: (options: {
    field: T;
    onChange: (field: T) => any;
    mustChooseValidation?: boolean;
    fieldNumber?: number;
    logicJumpOptions?: LogicJumpOption[];
    criterion?: Criterion;
    onCriterionChange?: (criterion: Criterion) => any;
    numberOfParticipantRequired?: number;
    sessionId?: string;
    quotas?: Quota[];
  }) => React.ReactElement;
  createDefaultProperties?: () => any;
}

type FieldsConfig = {
  [FieldType.SINGLE_CHOICE]: FieldConfig<SingleChoiceField>;
  [FieldType.MULTIPLE_CHOICE]: FieldConfig<MultipleChoiceField>;
  [FieldType.SHORT_TEXT]: FieldConfig<ShortTextField>;
  [FieldType.LONG_TEXT]: FieldConfig<LongTextField>;
  [FieldType.INFORMATION_PAGE]: FieldConfig<InformationPageField>;
};

export const validationFieldTypes = [
  FieldType.SINGLE_CHOICE,
  FieldType.MULTIPLE_CHOICE,
];

export const useFieldConfigurations = (): FieldsConfig => {
  const { t } = useTranslation();
  return {
    [FieldType.SHORT_TEXT]: {
      value: FieldType.SHORT_TEXT,
      label: t('screeningForm.create.questionBuilder.options.SHORT_TEXT'),
      icon: 'ri-text',

      renderPreview: ({ field, fieldNumber }) => (
        <PreviewFieldForm fieldNumber={fieldNumber}>
          <ShortText field={field} isPreview />
        </PreviewFieldForm>
      ),
    },
    [FieldType.LONG_TEXT]: {
      value: FieldType.LONG_TEXT,
      label: t('screeningForm.create.questionBuilder.options.LONG_TEXT'),
      icon: 'ri-paragraph',
      renderPreview: ({ field, fieldNumber }) => (
        <PreviewFieldForm fieldNumber={fieldNumber}>
          <LongText field={field} isPreview />
        </PreviewFieldForm>
      ),
    },
    [FieldType.MULTIPLE_CHOICE]: {
      label: t('screeningForm.create.questionBuilder.options.MULTIPLE_SELECT'),
      icon: 'ri-checkbox-line',
      value: FieldType.MULTIPLE_CHOICE,

      renderBuilder: (props) => (
        <MultipleChoiceBuilder
          field={props.field}
          onChange={props.onChange}
          criterion={props.criterion}
          onCriterionChange={props.onCriterionChange}
          logicJumpOptions={props.logicJumpOptions}
          mustChooseValidation={props.mustChooseValidation}
          numberOfParticipantRequired={props.numberOfParticipantRequired}
          sessionId={props.sessionId}
          quotas={props.quotas}
          onQuotaChange={props.onQuotaChange}
        />
      ),
      renderPreview: ({
        field,
        onChange,
        fieldNumber,
        logicJumpOptions,
        criterion,
        onCriterionChange,
        mustChooseValidation,
        sessionId,
        quotas,
      }) => (
        <PreviewFieldForm fieldNumber={fieldNumber}>
          <SelectChoicePreview
            field={field}
            sessionId={sessionId}
            onChange={onChange}
            logicJumpOptions={logicJumpOptions}
            criterion={criterion}
            onCriterionChange={onCriterionChange}
            mustChooseValidation={mustChooseValidation}
            enabledTolerate
            quotas={quotas?.filter((q) => q.ref === criterion?.fieldRef)}
          />
        </PreviewFieldForm>
      ),

      createDefaultProperties: () => ({
        randomize: false,
        allowMultipleSelection: true,
        autocomplete: false,
        choices: [
          {
            ref: uuidv4(),
            label: '',
          },
        ],
      }),
    },
    [FieldType.SINGLE_CHOICE]: {
      label: t('screeningForm.create.questionBuilder.options.SINGLE_SELECT'),
      icon: 'ri-radio-button-line',
      value: FieldType.SINGLE_CHOICE,

      renderBuilder: (props) => (
        <SelectChoiceBuilder<SingleChoiceField>
          field={props.field}
          onChange={props.onChange}
          criterion={props.criterion}
          quotas={props.criterion?.quotas?.map((q) => ({
            ref: props.criterion?.fieldRef,
            ...q,
          }))}
          onCriterionChange={props.onCriterionChange}
          logicJumpOptions={props.logicJumpOptions}
          mustChooseValidation={props.mustChooseValidation}
          numberOfParticipantRequired={props.numberOfParticipantRequired}
          sessionId={props.sessionId}
        />
      ),
      renderPreview: ({
        field,
        onChange,
        fieldNumber,
        logicJumpOptions,
        criterion,
        onCriterionChange,
        mustChooseValidation,
        numberOfParticipantRequired,
        sessionId,
      }) => (
        <PreviewFieldForm fieldNumber={fieldNumber}>
          <SelectChoicePreview
            field={field}
            sessionId={sessionId}
            onChange={onChange}
            logicJumpOptions={logicJumpOptions}
            criterion={criterion}
            quotas={criterion?.quotas}
            onCriterionChange={onCriterionChange}
            mustChooseValidation={mustChooseValidation}
            numberOfParticipantRequired={numberOfParticipantRequired}
          />
        </PreviewFieldForm>
      ),

      createDefaultProperties: () => ({
        randomize: false,
        allowMultipleSelection: false,
        autocomplete: false,
        choices: [
          {
            ref: uuidv4(),
            label: '',
          },
        ],
      }),
    },
    [FieldType.INFORMATION_PAGE]: {
      value: FieldType.INFORMATION_PAGE,
      label: t('screeningForm.create.questionBuilder.options.INFORMATION_PAGE'),
      icon: 'ri-information-line',
      renderPreview: ({ field, fieldNumber }) => (
        <InformationPage field={field} isPreview fieldNumber={fieldNumber} />
      ),
      renderBuilder: ({ field, onChange }) => (
        <InformationPageBuilder field={field} onChange={onChange} />
      ),
      createDefaultProperties: () => ({
        description: '',
      }),
    },
  };
};
